import { useState, useEffect } from 'react'
import {Calendar, Download, FileSpreadsheet, Printer, Plus, X, Mail, Save, Loader2, Check, Share2, Link2, BellRing, Sheet} from 'lucide-react'
import jsPDF from 'jspdf'
import * as XLSX from 'xlsx'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useRosterTemplates } from '@/hooks/useRosterTemplates'
import { useRosterPreferences } from '@/hooks/useRosterPreferences'
import { parseSharedRoster, describeSharedPattern } from '@/lib/rosterShare'
import ShareRosterDialog from '@/components/ShareRosterDialog'
import { buildRosterIcs, downloadRosterIcs } from '@/lib/rosterIcs'
import ReminderDialog from '@/components/ReminderDialog'
import SheetsExportDialog from '@/components/SheetsExportDialog'

export interface RosterPattern {
  cycles: Array<{daysOn: number, daysOff: number}>
  name?: string
}

interface RosterCalculatorProps {
  selectedTemplate: RosterPattern | null
}

interface RosterDay {
  date: Date
  isWorkDay: boolean
  cycleInfo?: string
}

export default function RosterCalculator({ selectedTemplate }: RosterCalculatorProps) {
  const { user, isAuthenticated } = useAuth()
  const { saveTemplate } = useRosterTemplates(user?.userId)
  const { preference, savePreference } = useRosterPreferences(user?.userId)

  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [pattern, setPattern] = useState<RosterPattern>({
    cycles: [{daysOn: 4, daysOff: 4}]
  })
  const [monthsToShow, setMonthsToShow] = useState<number>(3)
  const [roster, setRoster] = useState<RosterDay[]>([])
  const [showCustomBuilder, setShowCustomBuilder] = useState<boolean>(false)
  const [customCycles, setCustomCycles] = useState<Array<{daysOn: number, daysOff: number}>>([
    {daysOn: 4, daysOff: 4}
  ])
  const [preferenceLoaded, setPreferenceLoaded] = useState<boolean>(false)
  const [sharedConfig, setSharedConfig] = useState<{ startDate: string; monthsToShow: number; pattern: RosterPattern } | null>(null)

  // Open a shared link: apply the roster packed into the URL once on mount
  useEffect(() => {
    const shared = parseSharedRoster(window.location.search)
    if (shared) {
      setStartDate(shared.startDate)
      setMonthsToShow(shared.monthsToShow)
      setPattern(shared.pattern)
      setCustomCycles(shared.pattern.cycles)
      setSharedConfig({ startDate: shared.startDate, monthsToShow: shared.monthsToShow, pattern: shared.pattern })
      // A shared link should win over the visitor's own saved preference
      setPreferenceLoaded(true)
      // Bring the calculator into view for visitors arriving from a team link
      window.setTimeout(() => {
        document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [])

  // Load saved preference once when it becomes available for a logged-in user
  useEffect(() => {
    if (preference && !preferenceLoaded) {
      setStartDate(preference.startDate)
      setMonthsToShow(preference.monthsToShow)
      setPattern(preference.activePattern)
      setPreferenceLoaded(true)
    }
  }, [preference, preferenceLoaded])

  useEffect(() => {
    if (selectedTemplate) {
      setPattern(selectedTemplate)
      setShowCustomBuilder(false)
    }
  }, [selectedTemplate])

  useEffect(() => {
    calculateRoster()
  }, [startDate, pattern, monthsToShow])

  const calculateRoster = () => {
    const start = new Date(startDate)
    const totalDays = monthsToShow * 30
    const rosterDays: RosterDay[] = []

    // Calculate total cycle length
    const fullCycleLength = pattern.cycles.reduce((sum, cycle) => sum + cycle.daysOn + cycle.daysOff, 0)

    let dayCounter = 0
    for (let i = 0; i < totalDays; i++) {
      const currentDate = new Date(start)
      currentDate.setDate(start.getDate() + i)
      
      // Determine which cycle and position within that cycle
      const positionInFullCycle = dayCounter % fullCycleLength
      let accumulatedDays = 0
      let isWorkDay = false
      let currentCycleIndex = 0

      for (let c = 0; c < pattern.cycles.length; c++) {
        const cycle = pattern.cycles[c]
        const cycleLength = cycle.daysOn + cycle.daysOff
        
        if (positionInFullCycle < accumulatedDays + cycleLength) {
          const positionInThisCycle = positionInFullCycle - accumulatedDays
          isWorkDay = positionInThisCycle < cycle.daysOn
          currentCycleIndex = c
          break
        }
        accumulatedDays += cycleLength
      }

      rosterDays.push({
        date: currentDate,
        isWorkDay,
        cycleInfo: pattern.cycles.length > 1 ? `Cycle ${currentCycleIndex + 1}` : undefined
      })
      
      dayCounter++
    }

    setRoster(rosterDays)
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const getDayName = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  }

  const getPatternDescription = () => {
    if (pattern.cycles.length === 1) {
      return `${pattern.cycles[0].daysOn} days on, ${pattern.cycles[0].daysOff} days off`
    }
    return pattern.cycles.map((c, i) => 
      `Cycle ${i + 1}: ${c.daysOn} on/${c.daysOff} off`
    ).join(', ')
  }

  const exportToPDF = () => {
    const pdf = new jsPDF()
    pdf.setFontSize(18)
    pdf.text('Shift Roster Schedule', 20, 20)
    pdf.setFontSize(11)
    pdf.text(`Pattern: ${getPatternDescription()}`, 20, 30)
    pdf.text(`Start Date: ${new Date(startDate).toLocaleDateString()}`, 20, 37)
    
    let y = 50
    roster.forEach((day, index) => {
      if (y > 270) {
        pdf.addPage()
        y = 20
      }
      const dateStr = day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      const status = day.isWorkDay ? 'ON SHIFT' : 'OFF'
      pdf.text(`${dateStr} - ${status}`, 20, y)
      y += 7
    })
    
    pdf.save(`roster-schedule-${startDate}.pdf`)
  }

  const exportToExcel = () => {
    const excelData = roster.map(day => ({
      Date: day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      Day: getDayName(day.date),
      Status: day.isWorkDay ? 'ON SHIFT' : 'OFF'
    }))
    
    const ws = XLSX.utils.json_to_sheet(excelData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Roster')
    XLSX.writeFile(wb, `roster-schedule-${startDate}.xlsx`)
  }

  const exportToIcs = () => {
    const ics = buildRosterIcs({
      days: roster,
      patternLabel: pattern.name || getPatternDescription(),
      startDate,
      monthsToShow,
      calendarName: `${pattern.name || 'Shift Roster'} - Roster Calculator`
    })
    downloadRosterIcs(`roster-schedule-${startDate}.ics`, ics)
  }

  const handlePrint = () => {
    window.print()
  }

  const [savePrefStatus, setSavePrefStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  const handleSavePreference = async () => {
    if (!isAuthenticated) return
    setSavePrefStatus('saving')
    try {
      await savePreference({ startDate, monthsToShow, activePattern: pattern })
      setSavePrefStatus('saved')
      setTimeout(() => setSavePrefStatus('idle'), 2000)
    } catch (error) {
      console.error('Failed to save preference:', error)
      setSavePrefStatus('error')
      setTimeout(() => setSavePrefStatus('idle'), 2000)
    }
  }

  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState<boolean>(false)
  const [templateName, setTemplateName] = useState<string>('')
  const [saveTemplateStatus, setSaveTemplateStatus] = useState<{type: 'success' | 'error', message: string} | null>(null)

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSaveTemplateStatus({type: 'error', message: 'Please enter a template name'})
      return
    }
    if (!isAuthenticated) {
      setSaveTemplateStatus({type: 'error', message: 'Please log in to save custom templates'})
      return
    }
    try {
      await saveTemplate(templateName.trim(), pattern)
      setSaveTemplateStatus({type: 'success', message: 'Template saved! Find it in the sidebar.'})
      setTimeout(() => {
        setShowSaveTemplateDialog(false)
        setSaveTemplateStatus(null)
        setTemplateName('')
      }, 1800)
    } catch (error) {
      console.error('Failed to save template:', error)
      setSaveTemplateStatus({type: 'error', message: 'Failed to save template. Please try again.'})
    }
  }

  const [showShareDialog, setShowShareDialog] = useState<boolean>(false)
  const openShareDialog = () => setShowShareDialog(true)
  const [showReminderDialog, setShowReminderDialog] = useState<boolean>(false)
  const [showSheetsDialog, setShowSheetsDialog] = useState<boolean>(false)

  const [emailRecipient, setEmailRecipient] = useState<string>('')
  const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false)
  const [emailStatus, setEmailStatus] = useState<{type: 'success' | 'error', message: string} | null>(null)

  const sendRosterEmail = async () => {
    if (!emailRecipient) {
      setEmailStatus({type: 'error', message: 'Please enter a recipient email'})
      return
    }

    const user = lumi.auth.user
    if (!user) {
      setEmailStatus({type: 'error', message: 'Please log in to send emails'})
      return
    }

    try {
      // Build HTML email content
      const rosterRows = roster.slice(0, 60).map(day => {
        const dateStr = day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
        const status = day.isWorkDay ? 'ON SHIFT' : 'OFF'
        const bgColor = day.isWorkDay ? '#f97316' : '#f1f5f9'
        const textColor = day.isWorkDay ? '#ffffff' : '#334155'
        return `<tr><td style="padding: 8px; border: 1px solid #e2e8f0;">${dateStr}</td><td style="padding: 8px; border: 1px solid #e2e8f0; background-color: ${bgColor}; color: ${textColor}; font-weight: bold; text-align: center;">${status}</td></tr>`
      }).join('')

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; }
            .header { background-color: #1e293b; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #14b8a6; color: white; padding: 10px; text-align: left; }
            .footer { margin-top: 30px; padding: 15px; background-color: #f8fafc; border-top: 2px solid #e2e8f0; font-size: 12px; color: #64748b; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Your Shift Roster Schedule</h1>
          </div>
          <div class="content">
            <p><strong>Pattern:</strong> ${getPatternDescription()}</p>
            <p><strong>Start Date:</strong> ${new Date(startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            <p><strong>Duration:</strong> ${monthsToShow} month${monthsToShow > 1 ? 's' : ''}</p>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th style="text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${rosterRows}
              </tbody>
            </table>
            ${roster.length > 60 ? `<p style="margin-top: 15px; color: #64748b; font-style: italic;">Showing first 60 days of ${roster.length} total days</p>` : ''}
          </div>
          <div class="footer">
            <p>Generated by <strong>Roster Calculator</strong> - www.rostercalculator.com</p>
            <p>Professional shift planning for HR managers and shift workers</p>
          </div>
        </body>
        </html>
      `

      await lumi.tools.email.send({
        to: emailRecipient,
        subject: `Shift Roster Schedule - ${new Date(startDate).toLocaleDateString()}`,
        fromName: 'Roster Calculator',
        html: htmlContent
      })

      setEmailStatus({type: 'success', message: 'Roster sent successfully!'})
      setTimeout(() => {
        setShowEmailDialog(false)
        setEmailStatus(null)
        setEmailRecipient('')
      }, 2000)
    } catch (error) {
      console.error('Email send error:', error)
      setEmailStatus({type: 'error', message: 'Failed to send email. Ensure recipient is a registered user.'})
    }
  }

  return (
    <div id="calculator" className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Calendar className="w-7 h-7 text-teal-500" />
        <h2 className="text-2xl font-bold text-slate-900">Calculate Your Roster</h2>
      </div>

      {/* Input Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Months to Display
          </label>
          <select
            value={monthsToShow}
            onChange={(e) => setMonthsToShow(Number(e.target.value))}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value={1}>1 Month</option>
            <option value={3}>3 Months</option>
            <option value={6}>6 Months</option>
            <option value={12}>12 Months</option>
          </select>
        </div>
      </div>

      {/* Custom Pattern Builder */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => setShowCustomBuilder(!showCustomBuilder)}
          className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          {showCustomBuilder ? "Hide" : "Create"} Custom Pattern
        </button>

        {isAuthenticated && (
          <button
            onClick={handleSavePreference}
            disabled={savePrefStatus === 'saving'}
            className="px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {savePrefStatus === 'saving' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : savePrefStatus === 'saved' ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {savePrefStatus === 'saved' ? 'Saved!' : savePrefStatus === 'error' ? 'Failed' : 'Save My Preferences'}
          </button>
        )}
      </div>

      {showCustomBuilder && (
        <div className="mb-6 p-6 bg-slate-50 border border-slate-300 rounded-lg">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Custom Pattern Builder</h3>
          <p className="text-sm text-slate-600 mb-4">Build complex multi-cycle patterns (e.g., 8-6-7-7)</p>
          
          <div className="space-y-3 mb-4">
            {customCycles.map((cycle, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                <span className="text-sm font-medium text-slate-700 min-w-[60px]">Cycle {index + 1}:</span>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={cycle.daysOn}
                  onChange={(e) => {
                    const newCycles = [...customCycles]
                    newCycles[index].daysOn = Number(e.target.value)
                    setCustomCycles(newCycles)
                  }}
                  className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="On"
                />
                <span className="text-sm text-slate-600">days on,</span>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={cycle.daysOff}
                  onChange={(e) => {
                    const newCycles = [...customCycles]
                    newCycles[index].daysOff = Number(e.target.value)
                    setCustomCycles(newCycles)
                  }}
                  className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="Off"
                />
                <span className="text-sm text-slate-600">days off</span>
                {customCycles.length > 1 && (
                  <button
                    onClick={() => setCustomCycles(customCycles.filter((_, i) => i !== index))}
                    className="ml-auto p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    aria-label="Remove cycle"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setCustomCycles([...customCycles, {daysOn: 7, daysOff: 7}])}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Cycle
            </button>
            <button
              onClick={() => {
                setPattern({ cycles: customCycles })
                setShowCustomBuilder(false)
              }}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium"
            >
              Apply Pattern
            </button>
            {isAuthenticated && (
              <button
                onClick={() => {
                  setPattern({ cycles: customCycles })
                  setShowSaveTemplateDialog(true)
                }}
                className="px-4 py-2 bg-white border border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save as Template
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
        <p className="text-sm text-slate-700">
          <span className="font-semibold">Active Pattern:</span> {pattern.name || getPatternDescription()}
        </p>
      </div>

      {/* Shared roster notice */}
      {sharedConfig && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-300 rounded-lg flex flex-wrap items-center gap-3">
          <Link2 className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <p className="text-sm text-slate-800 flex-1 min-w-[200px]">
            <span className="font-semibold">Shared roster loaded.</span>{' '}
            {describeSharedPattern(sharedConfig.pattern)} starting{' '}
            {new Date(sharedConfig.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            . Adjust anything below and share your own version.
          </p>
          <button
            onClick={() => {
              setSharedConfig(null)
              const url = `${window.location.origin}${window.location.pathname}`
              window.history.replaceState({}, '', url)
            }}
            className="text-sm font-medium text-orange-700 underline hover:text-orange-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Calendar Display */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Your Roster Schedule</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button 
              onClick={exportToPDF}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
              aria-label="Export to PDF"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm"
              aria-label="Export to Excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={() => setShowSheetsDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors text-sm"
              aria-label="Export roster to Google Sheets"
              title="Create a Google spreadsheet from this roster"
            >
              <Sheet className="w-4 h-4" />
              Google Sheets
            </button>
            <button
              onClick={exportToIcs}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
              aria-label="Export to calendar (ICS file)"
              title="Download an .ics file for Outlook, Google Calendar or Apple Calendar"
            >
              <Calendar className="w-4 h-4" />
              Calendar
            </button>
            <button 
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
              aria-label="Print roster"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={() => setShowEmailDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
              aria-label="Email roster"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button
              onClick={() => (isAuthenticated ? setShowReminderDialog(true) : alert('Please sign in to schedule email reminders.'))}
              className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors text-sm"
              aria-label="Schedule email shift reminders"
            >
              <BellRing className="w-4 h-4" />
              Reminders
            </button>
            <button
              onClick={openShareDialog}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
              aria-label="Create shareable roster link"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {/* Calendar Grid */}
          {roster.slice(0, 42).map((day, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg text-center transition-all ${
                day.isWorkDay
                  ? 'bg-orange-500 text-white font-semibold shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
              title={day.cycleInfo || ''}
            >
              <div className="text-xs opacity-75">{getDayName(day.date)}</div>
              <div className="font-bold">{formatDate(day.date)}</div>
              <div className="text-xs mt-1">{day.isWorkDay ? 'ON' : 'OFF'}</div>
              {day.cycleInfo && pattern.cycles.length > 1 && (
                <div className="text-xs opacity-60 mt-0.5">{day.cycleInfo}</div>
              )}
            </div>
          ))}
        </div>

        {roster.length > 42 && (
          <p className="text-center text-sm text-slate-500 mt-4">
            Showing first 6 weeks. Total schedule: {monthsToShow} month{monthsToShow > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Email Dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">Email Roster</h3>
              <button
                onClick={() => {
                  setShowEmailDialog(false)
                  setEmailStatus(null)
                  setEmailRecipient('')
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Send this roster schedule to a registered user's email address.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recipient Email
              </label>
              <input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {emailStatus && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                emailStatus.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {emailStatus.message}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEmailDialog(false)
                  setEmailStatus(null)
                  setEmailRecipient('')
                }}
                className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={sendRosterEmail}
                disabled={!emailRecipient}
                className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Send Email
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Note: Recipient must be a registered user in your system.
            </p>
          </div>
        </div>
      )}

      {/* Save Template Dialog */}
      {showSaveTemplateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">Save Custom Template</h3>
              <button
                onClick={() => {
                  setShowSaveTemplateDialog(false)
                  setSaveTemplateStatus(null)
                  setTemplateName('')
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Save this pattern ({getPatternDescription()}) for quick access from the sidebar.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Template Name
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. My 8-6-7-7 Rotation"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {saveTemplateStatus && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                saveTemplateStatus.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {saveTemplateStatus.message}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveTemplateDialog(false)
                  setSaveTemplateStatus(null)
                  setTemplateName('')
                }}
                className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
                className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      <SheetsExportDialog
        isOpen={showSheetsDialog}
        onClose={() => setShowSheetsDialog(false)}
        patternLabel={pattern.name || getPatternDescription()}
        startDate={startDate}
        rows={roster.map((day) => ({
          date: `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}-${String(day.date.getDate()).padStart(2, '0')}`,
          status: day.isWorkDay ? 'ON SHIFT' : 'OFF',
        }))}
      />

      <ReminderDialog
        isOpen={showReminderDialog}
        onClose={() => setShowReminderDialog(false)}
        startDate={startDate}
        monthsToShow={monthsToShow}
        cycles={pattern.cycles}
        patternLabel={pattern.name || getPatternDescription()}
        userEmail={user?.email}
      />

      {/* Share Link Dialog */}
      <ShareRosterDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        config={{ startDate, monthsToShow, pattern }}
      />
    </div>
  )
}
